// PRD 08 §6 — confirm before group delete. Per user answer #4 group delete
// cascades on the backend (matches prototype); no link-block here. Code-level
// link-block lives in Slice 9.

import { useTranslation } from 'react-i18next';

import { ConfirmModal } from '@/components/feedback/ConfirmModal';
import { toast } from '@/components/feedback/useToast';

import { useDeleteDiseaseGroup } from '../hooks/use-disease-code-mutations';
import { useDiseaseCodePageStore } from '../store/disease-code-page.store';
import type { DiseaseGroup } from '../types/disease-code.types';
import { getDiseaseCodeErrorMessage } from '../utils/error-message';

interface DeleteDiseaseGroupConfirmProps {
  group: DiseaseGroup | null;
  onClose: () => void;
}

export function DeleteDiseaseGroupConfirm({ group, onClose }: DeleteDiseaseGroupConfirmProps) {
  const { t } = useTranslation('admin');
  const deleteMutation = useDeleteDiseaseGroup();
  const selectedGroupId = useDiseaseCodePageStore((s) => s.selectedGroupId);
  const selectGroup = useDiseaseCodePageStore((s) => s.selectGroup);

  const handleConfirm = async () => {
    if (!group || deleteMutation.isPending) return;
    try {
      await deleteMutation.mutateAsync(group.id);
      // Clear page selection so the right panel collapses back to its empty
      // state (matches prototype `confirmDeleteGroup`).
      if (selectedGroupId === group.id) selectGroup(null);
      toast.success(t('diseaseCodeManagement.toasts.groupDeleted'));
      onClose();
    } catch (e) {
      toast.error(
        getDiseaseCodeErrorMessage(e, t, 'diseaseCodeManagement.toasts.deleteError'),
      );
    }
  };

  return (
    <ConfirmModal
      open={group !== null}
      title={t('diseaseCodeManagement.deleteGroup.title')}
      message={
        group
          ? t('diseaseCodeManagement.deleteGroup.message', { name: group.name })
          : ''
      }
      confirmLabel={t('diseaseCodeManagement.deleteGroup.confirm')}
      cancelLabel={t('diseaseCodeManagement.deleteGroup.cancel')}
      confirmVariant="destructive"
      loading={deleteMutation.isPending}
      onConfirm={handleConfirm}
      onCancel={() => {
        if (deleteMutation.isPending) return;
        onClose();
      }}
    />
  );
}
